import type { PosAccountSnapshot } from "./types";

/** Amount still owed by the guest. A negative value is money owed to the guest. */
export function outstandingBalanceMinor(
  account: Pick<PosAccountSnapshot, "totalAmountMinor" | "paidAmountMinor">,
): number {
  return account.totalAmountMinor - account.paidAmountMinor;
}

export function refundDueMinor(
  account: Pick<PosAccountSnapshot, "totalAmountMinor" | "paidAmountMinor">,
): number {
  return Math.max(0, -outstandingBalanceMinor(account));
}

/** Preview the balance after selected negative deposit lines are booked. */
export function projectedOutstandingBalanceMinor(
  account: Pick<PosAccountSnapshot, "totalAmountMinor" | "paidAmountMinor">,
  depositCreditMinor: number,
): number {
  return outstandingBalanceMinor(account) - depositCreditMinor;
}

export function projectedRefundDueMinor(
  account: Pick<PosAccountSnapshot, "totalAmountMinor" | "paidAmountMinor">,
  depositCreditMinor: number,
): number {
  return Math.max(
    0,
    -projectedOutstandingBalanceMinor(account, depositCreditMinor),
  );
}

export type TerminalBalanceTransaction = {
  type: "payment" | "refund";
  amountMinor: number;
};

/**
 * Positive outstanding balances are collected; negative balances are paid
 * back. A settled account does not require a terminal transaction.
 */
export function terminalTransactionForBalance(
  outstandingMinor: number,
): TerminalBalanceTransaction | null {
  if (!Number.isSafeInteger(outstandingMinor))
    throw new Error("Outstanding balance must be safe integer minor units");
  if (outstandingMinor === 0) return null;
  return {
    type: outstandingMinor > 0 ? "payment" : "refund",
    amountMinor: Math.abs(outstandingMinor),
  };
}

export function checkoutRequired(
  account: Pick<PosAccountSnapshot, "totalAmountMinor" | "paidAmountMinor">,
): boolean {
  return outstandingBalanceMinor(account) >= 0;
}

/**
 * The maximum is checked after calculating the net account balance, so partial
 * payments are correctly retained and cannot be refunded a second time.
 */
export function enforceMaxRefundMinor(
  amountMinor: number,
  maxRefundMinor: number,
): number {
  if (
    !Number.isSafeInteger(amountMinor) ||
    !Number.isSafeInteger(maxRefundMinor)
  ) {
    throw new Error("Refund amounts must be safe integer minor units");
  }
  if (amountMinor < 0 || maxRefundMinor < 0)
    throw new Error("Refund amounts cannot be negative");
  if (amountMinor > maxRefundMinor)
    throw new Error(
      `Refund ${amountMinor} exceeds configured maximum ${maxRefundMinor}`,
    );
  return amountMinor;
}
